import * as React from 'react';
import {Suspense, useState} from 'react';
import {graphql, useLazyLoadQuery} from 'react-relay';
import {Link, useParams} from 'react-router-dom';
import {ArrowLeft, Check, Minus, Plus, ShoppingBag} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {addToCart, removeFromCart, useCart} from '../cart';
import {GearPageQuery} from './__generated__/GearPageQuery.graphql';

const QUERY = graphql`
    query GearPageQuery($assetTag: AssetTag!, $startOn: ISO8601Date!, $endOn: ISO8601Date!) {
        asset(assetTag: $assetTag) {
            id
            assetTag
            name
            make
            model
            description
            category
            image
            dailyRate
            specifications
            properties { key value }
            available: availableOn(startOn: $startOn, endOn: $endOn)
            kit { assetTag name }
            kitMembers { id assetTag name category image }
        }
    }`;

const PROPERTY_LABEL: Record<string, string> = {
    mount: 'Mount', aperture: 'Aperture', capacity: 'Capacity', interface: 'Interface', mah: 'Capacity (mAh)',
    subtype: 'Type', cable_type: 'Cable', length: 'Length', type: 'Type', units: 'Units', connector: 'Connector',
};

const fmtDay = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, {weekday: 'short', month: 'short', day: 'numeric'});

const Product: React.FC<{ tag: string }> = ({tag}) => {
    const cart = useCart();
    const data = useLazyLoadQuery<GearPageQuery>(QUERY, {assetTag: tag, startOn: cart.startOn, endOn: cart.endOn});
    const asset = data.asset;
    const [justAdded, setJustAdded] = useState(false);

    if (!asset) {
        return (
            <div className="text-center py-20">
                <p className="text-gray-400">We don't list anything under <span className="font-mono text-white">{tag}</span>.</p>
                <Link to="/book" className="text-lumina-accent text-sm">Browse the gear →</Link>
            </div>
        );
    }

    const inCart = cart.items.some((i) => i.assetTag === asset.assetTag);
    const toggle = () => {
        if (inCart) removeFromCart(asset.assetTag);
        else {
            addToCart({assetTag: asset.assetTag, name: asset.name, dailyRate: asset.dailyRate, pieces: asset.kitMembers.length});
            setJustAdded(true);
            setTimeout(() => setJustAdded(false), 1500);
        }
    };
    const properties = asset.properties.filter((p) => !['mac', 'ecid'].includes(p.key));

    return (
        <div>
            <Link to="/book" className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-white mb-6"><ArrowLeft className="w-3 h-3"/> All gear</Link>

            <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10">
                <div className="aspect-[4/3] bg-gray-900 border border-gray-800 flex items-center justify-center overflow-hidden">
                    {asset.image
                        ? <img src={asset.image} alt={asset.name} className="w-full h-full object-cover"/>
                        : <span className="font-mono text-xs text-gray-700 tracking-widest">{asset.assetTag}</span>}
                </div>

                <div>
                    <div className="flex items-center gap-2 mb-2">
                        <span className="bg-lumina-purple/90 text-white text-[10px] font-bold px-2 py-0.5 uppercase">{asset.category.replace(/_/g, ' ')}</span>
                        {asset.kit && <Link to={`/gear/${asset.kit.assetTag}`} className="text-[10px] text-gray-400 border border-gray-800 px-2 py-0.5 uppercase hover:text-white">part of {asset.kit.name}</Link>}
                    </div>
                    <h1 className="text-3xl md:text-4xl font-display font-bold leading-tight">{asset.name}</h1>
                    <p className="font-mono text-xs text-gray-500 mt-1">{asset.assetTag}</p>

                    {asset.description && asset.model && <p className="text-gray-300 mt-4">{asset.description}</p>}

                    {asset.specifications.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-4">
                            {asset.specifications.map((s, i) => (
                                <span key={i} className="text-[11px] text-gray-300 bg-gray-900 px-2 py-1 border border-gray-800">{s}</span>
                            ))}
                        </div>
                    )}

                    <div className="mt-6 border-t border-gray-800 pt-5 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <span className="text-[10px] text-gray-500 uppercase tracking-wider">{asset.dailyRate ? 'Daily rate' : 'Pricing'}</span>
                            <div className="text-2xl font-display font-bold text-lumina-accent">
                                {asset.dailyRate ? <>${asset.dailyRate}<span className="text-gray-600 text-sm font-sans font-normal">/day</span></> : <span className="text-gray-300 text-lg">On request</span>}
                            </div>
                            <p className={`text-xs mt-1 ${asset.available ? 'text-emerald-400' : 'text-amber-400'}`}>
                                {asset.available ? 'Available' : 'Booked'} {fmtDay(cart.startOn)} → {fmtDay(cart.endOn)}
                                <Link to="/book" className="text-gray-500 hover:text-white"> · change dates</Link>
                            </p>
                        </div>
                        <button onClick={toggle} disabled={!inCart && !asset.available}
                                className={`inline-flex items-center justify-center gap-2 px-6 py-3 text-xs font-bold tracking-widest uppercase transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                                    inCart ? 'bg-lumina-accent text-black' : 'bg-white text-black hover:bg-lumina-accent'}`}>
                            {inCart ? <><Minus className="w-4 h-4"/> In your request</> : justAdded ? <><Check className="w-4 h-4"/> Added</> : <><Plus className="w-4 h-4"/> Add to request</>}
                        </button>
                    </div>
                    {cart.items.length > 0 && (
                        <Link to="/book" className="mt-3 inline-flex items-center gap-2 text-xs text-gray-400 hover:text-white">
                            <ShoppingBag className="w-3 h-3"/> {cart.items.length} item{cart.items.length === 1 ? '' : 's'} in your request →
                        </Link>
                    )}
                </div>
            </div>

            {properties.length > 0 && (
                <section className="mt-12">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">Details</h2>
                    <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-800 border border-gray-800">
                        {asset.make && <div className="bg-lumina-dark p-4"><dt className="text-[10px] text-gray-500 uppercase tracking-wider">Make</dt><dd className="text-white">{asset.make}</dd></div>}
                        {asset.model && <div className="bg-lumina-dark p-4"><dt className="text-[10px] text-gray-500 uppercase tracking-wider">Model</dt><dd className="text-white">{asset.model}</dd></div>}
                        {properties.map((p) => (
                            <div key={p.key} className="bg-lumina-dark p-4">
                                <dt className="text-[10px] text-gray-500 uppercase tracking-wider">{PROPERTY_LABEL[p.key] ?? p.key.replace(/_/g, ' ')}</dt>
                                <dd className="text-white">{p.value}</dd>
                            </div>
                        ))}
                    </dl>
                </section>
            )}

            {asset.kitMembers.length > 0 && (
                <section className="mt-12">
                    <h2 className="text-xs uppercase tracking-widest text-gray-500 mb-3">What's in the kit · {asset.kitMembers.length} pieces</h2>
                    <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {asset.kitMembers.map((m) => (
                            <li key={m.id}>
                                <Link to={`/gear/${m.assetTag}`} className="flex items-center gap-3 border border-gray-800 p-3 hover:border-lumina-purple/50">
                                    <span className="w-14 h-14 bg-gray-900 flex items-center justify-center overflow-hidden shrink-0">
                                        {m.image ? <img src={m.image} alt="" className="w-full h-full object-cover"/> : <span className="text-[8px] font-mono text-gray-700">{m.assetTag}</span>}
                                    </span>
                                    <span className="min-w-0">
                                        <span className="block text-white text-sm truncate">{m.name}</span>
                                        <span className="block text-[11px] text-gray-500">{m.category.replace(/_/g, ' ')}</span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
};

const GearPage: React.FC = () => {
    const {tag = ''} = useParams();
    return (
        <div className="bg-lumina-dark min-h-screen text-white">
            <Navbar/>
            <main className="max-w-7xl mx-auto px-6 pt-28 pb-24">
                <Suspense fallback={<p className="text-gray-500 text-sm">Loading…</p>}>
                    <Product tag={tag.toUpperCase()}/>
                </Suspense>
            </main>
            <Footer/>
        </div>
    );
};

export default GearPage;
